import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Grid } from '@/common/components/Grid/Grid';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { TPlan } from '@/common/constants/markets';
import { getPersonalFieldsByPlan } from '@/common/containers/Dashboard/Profile/ProfilePersonalDetailsPage/ProfilePersonalDetailsPage.utils';
import { ProfileCard } from '@/common/containers/Dashboard/Profile/components/ProfileCard/ProfileCard';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { IEmployee } from '@/common/integrations/menhir/employee/employee/employee.types';
import { formatDate } from '@/common/utils/date';
import { TaxUtils } from '@/common/utils/tax';
import { useCountries } from '@/web/hooks/useCountries';

export type TYourDetailsCardProps = {
  employee: IEmployee;
  plan: TPlan;
};

export function YourDetailsCard({ employee, plan }: TYourDetailsCardProps) {
  const { t } = useTranslation();
  const { getCountryByCode } = useCountries();
  const nationalityName =
    getCountryByCode(employee.nationality)?.name ?? employee.nationality;
  const fields = getPersonalFieldsByPlan(plan);

  return (
    <ProfileCard title={t('dashboard.personal_info.sections.personal.title')}>
      <LabeledValue
        label={t('dashboard.personal_info.sections.personal.labels.full_name')}
        value={employee.name}
        valueTestId="fullNameField"
      />
      <Grid columnBasis="basis-[14rem]" gap="lg">
        <LabeledValue
          label={t('dashboard.personal_info.sections.personal.labels.tax_id')}
          value={TaxUtils.formatTaxId(employee.taxId)}
          valueTestId="taxField"
        />
        {fields.gender && employee.gender && (
          <LabeledValue
            label={t('dashboard.personal_info.sections.personal.labels.gender')}
            value={
              employee.gender ? t(`general.gender.${employee.gender}`) : ''
            }
            valueTestId="genderField"
          />
        )}
        {fields.birthdate && employee.birthDate && (
          <LabeledValue
            label={t(
              'dashboard.personal_info.sections.personal.labels.birthday_date'
            )}
            value={formatDate(employee.birthDate)}
            valueTestId="birthdateField"
          />
        )}
        {fields.nationality && (
          <LabeledValue
            label={t(
              'dashboard.personal_info.sections.personal.labels.nationality'
            )}
            value={nationalityName}
          />
        )}
        {fields.birthplace && employee.birthplace ? (
          <LabeledValue
            label={t(
              'dashboard.personal_info.sections.personal.labels.birthplace'
            )}
            value={employee.birthplace}
            valueTestId="birthplaceField"
          />
        ) : null}
        {fields.fiscalRegion ? (
          <LabeledValue
            label={t(
              'dashboard.personal_info.sections.personal.labels.fiscal_region'
            )}
            value={employee.fiscalRegionName}
            valueTestId="fiscalRegionField"
          />
        ) : null}
      </Grid>
      <BodyText size="sm" color="neutral20">
        <Translate i18nKey="dashboard.personal_info.help_disclaimer">
          &nbsp;
          <TextAction
            onPress={() => {
              AnalyticsService.openChat();
            }}
          >
            &nbsp;
          </TextAction>
        </Translate>
      </BodyText>
    </ProfileCard>
  );
}
